import { Permission } from '@bookora/shared';

import { joinFa, quoted } from './text';
import type { StepContext, TourDef } from './types';

const RESERVED = new Set(['new', 'recycle-bin', 'import']);

/** `/patients/:id`, and not `/patients/new`, `/patients/import` or the recycle bin. */
export function isPatientFilePath(pathname: string): boolean {
  const match = /^\/patients\/([^/]+)$/.exec(pathname);
  return Boolean(match && !RESERVED.has(match[1]));
}

/**
 * The buttons of «کارهای این بیمار» (`patient-next-steps.tsx`), each behind
 * the key it is drawn with, in the row's order.
 */
function nextStepLabels(ctx: StepContext): string[] {
  return [
    ctx.can(Permission.SESSION_CREATE) ? 'ثبت جلسه' : null,
    ctx.can(Permission.APPOINTMENT_CREATE) ? 'ثبت نوبت' : null,
    ctx.can(Permission.FINANCE_RECORD_PAYMENT) ? 'ثبت دریافت' : null,
    ctx.can(Permission.PACKAGE_VIEW) && ctx.can(Permission.PACKAGE_SELL) ? 'فروش بسته خدمات' : null,
  ]
    .filter((x): x is string => x !== null)
    .map(quoted);
}

/**
 * Version 2: `actions` now spotlights the «کارهای این بیمار» row (ثبت جلسه،
 * ثبت نوبت، ثبت دریافت، فروش بسته خدمات) instead of the header's ویرایش / «بیشتر»
 * and the tour chip, and `maxSteps` is 5 so the financial step is no longer
 * cut for an owner (the alerts anchor is an empty, too-small box on a file
 * with nothing to warn about, and is skipped at runtime).
 */
export const PATIENT_FILE_TOUR: TourDef = {
  key: 'patient-file',
  version: 2,
  surface: 'page',
  title: 'پرونده بیمار',
  hubDescription: 'کارهای این بیمار، سابقهٔ درمان، رضایت‌نامه‌ها و وضعیت مالی.',
  offer: {
    body: 'در چند قدم کوتاه نشونتون می‌دیم سابقه، رضایت‌نامه‌ها و کارهای پرتکرار هر بیمار کجاست.',
  },
  route: { path: '/patients', match: isPatientFilePath, context: 'patient' },
  requires: { all: [Permission.PATIENT_VIEW] },
  autoOffer: true,
  maxSteps: 5,
  minSteps: 2,
  priority: ['alerts', 'actions', 'timeline', 'consents', 'financial'],
  steps: [
    {
      id: 'alerts',
      anchor: 'patient.alerts',
      placement: 'bottom',
      title: 'هشدارهای بیمار',
      body: 'حساسیت‌ها و نکته‌های مهم بیمار بالای پرونده می‌مونه تا موقع بررسی پرونده راحت ببینیدشون.',
    },
    {
      // The row renders nothing for someone who holds none of its keys, so
      // the step skips itself; `when` keeps the copy from naming nothing.
      id: 'actions',
      anchor: 'patient.next-steps',
      placement: 'bottom',
      when: (ctx) => nextStepLabels(ctx).length > 0,
      title: 'کارهای این بیمار',
      body: (ctx) => {
        const labels = nextStepLabels(ctx);
        if (labels.length === 0) return 'کارهای این بیمار از این ردیف انجام می‌شه.';
        return labels.length === 1
          ? `${joinFa(labels)} برای همین بیمار از این ردیف انجام می‌شه.`
          : `${joinFa(labels)} برای همین بیمار از این ردیف انجام می‌شن.`;
      },
    },
    {
      id: 'timeline',
      anchor: 'patient.timeline',
      placement: 'auto',
      title: 'سابقهٔ درمان',
      body: 'جلسه‌های درمان، نوبت‌ها و رضایت‌نامه‌های این بیمار به ترتیب زمان اینجا دیده می‌شه.',
    },
    {
      id: 'consents',
      anchor: 'patient.consents',
      placement: 'auto',
      requires: { all: [Permission.CONSENT_VIEW] },
      title: 'رضایت‌نامه‌ها',
      body: (ctx) =>
        ctx.can(Permission.CONSENT_CREATE)
          ? 'رضایت‌نامه رو با پیامک برای امضا بفرستید، حضوری امضا بگیرید یا نسخهٔ کاغذی رو پیوست کنید.'
          : 'رضایت‌نامه‌های این بیمار و وضعیت امضای هر کدام اینجاست.',
    },
    {
      id: 'financial',
      anchor: 'patient.financial',
      placement: 'auto',
      requires: { all: [Permission.FINANCE_VIEW_REVENUE] },
      title: 'وضعیت مالی بیمار',
      body: (ctx) =>
        ctx.can(Permission.FINANCE_RECORD_PAYMENT)
          ? 'مانده‌حساب و دریافت‌های این بیمار اینجاست. دریافت جدید رو هم از همین‌جا ثبت کنید.'
          : 'مانده‌حساب و دریافت‌های این بیمار اینجاست.',
    },
  ],
};
